import { Component, OnDestroy, OnInit, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  FilterParams,
  ClobSettingsObj
} from '../f1065-k2-data-entry.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f1065-k2-header',
    templateUrl: './f1065-k2-header.component.html',
    styleUrls: ['../f1065-k2.styles.scss', './f1065-k2-header.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065K2HeaderComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  // Configuration - unique per component
  private lineDetailsActionCode = 'w5bepp';
  sectionHeader = 'Header Information:';

  // Data
  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};
  
  colHeaderData: ColumnHeaderData[] = [];
  colHeaderSections: Set<string> = new Set();
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];
  
  // State
  loadingData = true;
  showLoader = true;
  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isValidationError = false;
  isInitialized = false;

  // Counters for dropdown loading
  totalDropDownActions = 0;
  loadedActionCounter = 0;

  // Labels
  currentEntityName = '';
  currentPartnerName = '';
  currentCodeGroup = '';
  formSchd = '';

  // Keys
  jcdKey = 0;
  scenario = '';

  // Screen link for dynamic code group
  screenLink = '';

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };

  private isLoading = false;

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    // Explicit initial call — tryInit() has internal dedup (lastContextKey + isLoading),
    // so redundant calls from BehaviorSubject replay are harmless.
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.screenLink = activeScreen?.link || this.screenLink || '';
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  private tryInit(): void {
    if (this.isLoading) return;

    const hasGlobalService = this.globalService && this.globalService.globalParams;
    const hasBaseUrl = this.GENERAL_CONFIG?.base_url || (this.baseURLs && this.baseURLs.api);
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasScreenLink = !!this.screenLink;

    if (!hasGlobalService || !hasBaseUrl || !hasFilterParams || !hasScreenLink) {
      return;
    }

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.screenLink
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    this.init();
  }

  private reloadData(): void {
    this.isLoading = false;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.groupedFormLines = [];
    this.colHeaderData = [];
    this.colHeaderSections.clear();
    this.showErrCol = false;
    this.isValidationError = false;
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    
    this.init();
  }

  private init(): void {
    this.isInitialized = true;
    this.scenario = this.filterParams.scenario || '';
    
    this.setCurrentCodeGroup();
    this.loadLockStatus();
  }

  private getAfterUnderscore(str: string): string {
    const idx = str.indexOf('_');
    return idx >= 0 ? str.substring(idx) : '';
  }

  private setCurrentCodeGroup(): void {
    const linkPart = this.getAfterUnderscore(this.screenLink);
    this.currentCodeGroup = `${this.filterParams.p_form}_${this.filterParams.p_schedule}${linkPart}`;
    this.formSchd = `${this.filterParams.p_form}_${this.filterParams.p_schedule}`;
  }

  private loadLockStatus(): void {
    this.service.getLockStatus(
      this.baseURLs.api,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (data && data.length > 0 && data[0].LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
        this.loadColumnHeaders();
      },
      error: (err) => {
        console.error('[F1065K2-Header] Error loading lock status:', err);
        this.loadColumnHeaders();
      }
    });
  }

  private loadColumnHeaders(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();

    this.service.getColumnHeaders(
      this.baseURLs.api,
      this.filterParams.tax_year || '',
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.colHeaderData = data || [];
        
        if (this.colHeaderData.length === 0) {
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.showLoader = false;
          this.isLoading = false;
          return;
        }
        
        // Build colHeaderSections and check for dropdowns
        let isDropdownAvailable = false;
        this.colHeaderSections.clear();
        
        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
          if (col.COL_SEC) {
            this.colHeaderSections.add(col.COL_SEC);
          }
          if (col.DROP_DOWN_ACTION) {
            isDropdownAvailable = true;
            this.totalDropDownActions++;
            this.loadDropdownData(col);
          }
        }
        
        if (!isDropdownAvailable) {
          this.loadLineDetails();
        }
      },
      error: (err) => {
        console.error('[F1065K2-Header] Error loading column headers:', err);
        this.service.showError('Error loading column headers');
        this.showLoader = false;
        this.isLoading = false;
      }
    });
  }

  private loadDropdownData(colData: ColumnHeaderData): void {
    if (!colData.DROP_DOWN_ACTION) return;
    
    const actionDetails = colData.DROP_DOWN_ACTION.split(':');
    
    this.service.getDropdownData(
      this.baseURLs.api,
      actionDetails[0],
      actionDetails[1],
      this.filterParams.tax_year || '',
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        colData.actionData = data || [];
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
      },
      error: (err) => {
        console.error('[F1065K2-Header] Error loading dropdown data:', err);
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
      }
    });
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    const pFilerKey = this.filterParams.p_filer_key;
    if (pFilerKey === null || pFilerKey === undefined || (typeof pFilerKey === 'number' && pFilerKey < 0)) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      return;
    }

    const isK3 = this.currentCodeGroup.includes('_K3');
    const pFilerPartnerKey = this.filterParams.p_filer_partner_key;
    if (isK3) {
      const partnerKeyInvalid = pFilerPartnerKey === null || pFilerPartnerKey === undefined ||
        (typeof pFilerPartnerKey === 'number' && pFilerPartnerKey < 0) ||
        (typeof pFilerPartnerKey === 'string' && (!pFilerPartnerKey || pFilerPartnerKey.trim() === ''));
      
      if (partnerKeyInvalid) {
        this.service.showError('Please select all the filters');
        this.showLoader = false;
        this.isLoading = false;
        return;
      }
    }

    // Set entity and partner labels for confirmation modal
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (isK3) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }

    this.service.getLineDetails(
      this.baseURLs.api,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      pFilerKey,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);
        
        // Calculate totals on form load (empty for Header)
        for (const line of this.groupedFormLines) {
          this.calcTotalsCol(line.items);
        }

        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.showErrCol = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-Header] Error loading line details:', err);
        this.service.showError('Error loading line details');
        this.showLoader = false;
        this.isLoading = false;
      }
    });
  }

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (formLines && formLines.length > 0) {
      this.isAdmin = true;
    }

    const grouped: GroupedFormLine[] = [];

    for (const property of formLines) {
      let bucket = grouped.find(b =>
        b.SCHD_KEY === property.SCHD_KEY &&
        b.LINE_KEY === property.LINE_KEY &&
        b.LINE_NO === property.LINE_NO &&
        b.LINE_DESC === property.LINE_DESC &&
        b.LINE_ORDER === property.LINE_ORDER &&
        b.SNO === property.SNO &&
        b.IS_LINE_MULTI === property.IS_LINE_MULTI &&
        b.LINE_SEC === property.LINE_SEC
      );

      if (!bucket) {
        bucket = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI,
          LINE_SEC: property.LINE_SEC,
          ERR_MSG: undefined,
          items: []
        };
        grouped.push(bucket);
      }

      const attribs: FormLineItem = {
        SCHD_KEY: property.SCHD_KEY,
        LINE_KEY: property.LINE_KEY,
        COL_KEY: property.COL_KEY,
        COL_DESC: property.COL_DESC,
        COL_ORDER: property.COL_ORDER,
        IS_ENABLED: property.EDITABLE_Y_N,
        SNO: property.SNO,
        ADJ_KEY: property.ADJ_KEY,
        IS_COL_MULTI: property.IS_COL_MULTI,
        UI_COMP: property.UI_COMP,
        ACTION_Y_N: property.ACTION_Y_N,
        CELL_VALUE: property.CELL_VALUE,
        IS_MANDATORY: 'N',
        OPERATION_TYPE: undefined,
        IS_ERROR: 'N'
      };

      // Set action data if available
      if (attribs.ACTION_Y_N === 'Y') {
        const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
        if (colObj) {
          attribs.actionData = colObj.actionData;
        }
      }

      // Set validation info
      this.setValidationInfo(property, attribs);

      // Set operation type
      if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
        attribs.OPERATION_TYPE = 'U';
      } else {
        attribs.OPERATION_TYPE = undefined;
      }

      // Set defaults for multi-line empty rows
      if (property.SNO === 0 && property.IS_LINE_MULTI === 'Y' && attribs.CELL_VALUE === null) {
        attribs.CELL_VALUE = '';
      }

      // Convert DATE string to Date object (using parseAsLocalDate to avoid timezone issues)
      if (attribs.UI_COMP === 'DATE') {
        if (attribs.CELL_VALUE !== 0 && attribs.CELL_VALUE) {
          attribs.CELL_VALUE = parseAsLocalDate(property.CELL_VALUE);
        } else {
          attribs.CELL_VALUE = '';
        }
      }

      // Mark non-editable cells as not to be saved
      if (this.isSkipFromSaving(bucket, attribs)) {
        attribs.OPERATION_TYPE = 'N';
      }

      bucket.items.push(attribs);
    }

    return grouped;
  }

  private setValidationInfo(source: any, dest: any): void {
    if (source.VALIDATION_RULE != null) {
      const validationRulesArr = source.VALIDATION_RULE.split(';');
      for (const rule of validationRulesArr) {
        if (rule === 'REQ') {
          dest.IS_MANDATORY = 'Y';
        } else if (rule.includes('MAX_LEN:')) {
          dest.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  private isNotEmpty(item: FormLineItem): boolean {
    return item.CELL_VALUE !== undefined && item.CELL_VALUE !== null && item.CELL_VALUE !== 0 && item.CELL_VALUE !== '';
  }

  private isEmpty(item: FormLineItem): boolean {
    return !this.isNotEmpty(item);
  }

  private isAnyOtherValueEntered(item: FormLineItem, itemList: FormLineItem[]): boolean {
    return itemList.some(x => x !== item && x.IS_ENABLED === 'Y' && this.isNotEmpty(x));
  }

  // Header: Empty function - no column totals calculation
  calcTotalsCol(columnValues: FormLineItem[]): void {
    // No calculation needed for Header
  }

  /**
   * Check if item is the last NUMERIC column
   * Header doesn't have auto-calculated totals, so always return false
   */
  isLastNumericColumn(items: FormLineItem[], item: FormLineItem): boolean {
    return false; // Header has no auto-calculated Total column
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const multiRows = this.groupedFormLines.filter(
      l => l.LINE_KEY === selectedRowData.LINE_KEY && l.IS_DISPLAY === 'Y'
    );
    const sno = multiRows[multiRows.length - 1].SNO + 1;

    const tempObj: GroupedFormLine = JSON.parse(JSON.stringify(selectedRowData));
    tempObj.LINE_DESC = '';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    for (const item of tempObj.items) {
      item.OPERATION_TYPE = 'I';
      item.ADJ_KEY = null;
      // Header uses different defaults
      if (item.UI_COMP === 'DATE') {
        item.CELL_VALUE = '';
      } else {
        item.CELL_VALUE = 0;
      }
      item.SNO = tempObj.SNO;
    }

    this.groupedFormLines.splice(index + sno + 1, 0, tempObj);
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;
    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }
    if (!isRowSaved) {
      const idx = this.groupedFormLines.indexOf(selectedRowData);
      if (idx > -1) {
        this.groupedFormLines.splice(idx, 1);
      }
    }
  }

  reset(): void {
    // Clear array before reload to prevent rendering stale data during async fetch
    this.groupedFormLines = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    
    this.loadLineDetails();
  }

  saveData(): void {
    this.isSaveClicked = true;

    this.validateForm(this.groupedFormLines);

    if (this.isValidationError) {
      this.service.showError('Please fix all error(s)');
      this.isSaveClicked = false;
      return;
    }

    // Show confirmation dialog
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    this.showConfirmModal = true;
  }

  onConfirmSave(confirmed: boolean): void {
    this.showConfirmModal = false;
    if (confirmed) {
      this.performSave();
    } else {
      this.isSaveClicked = false;
    }
  }

  private performSave(): void {
    const dataToSave = this.buildDataToSave(this.groupedFormLines);

    const returnClobSettingsObj: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    // Call getLockStatus before save (matching original AngularJS behavior)
    this.service.getLockStatus(
      this.baseURLs.api,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Proceed with save after lock status check
        this.service.saveData(
          this.baseURLs.api,
          returnClobSettingsObj,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result?.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Save failed';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
          },
          error: (err) => {
            console.error('[F1065K2-Header] Error saving data:', err);
            this.service.showError('Error saving data');
            this.isSaveClicked = false;
          }
        });
      },
      error: (err) => {
        console.error('[F1065K2-Header] Lock status check error:', err);
        this.isSaveClicked = false;
      }
    });
  }

  private validateForm(groupedFormLinesObj: GroupedFormLine[]): void {
    this.isValidationError = false;

    for (const line of groupedFormLinesObj) {
      line.ERR_MSG = undefined;
      let requiredMsgAlreadyAdded = false;

      // Reset errors
      for (const item of line.items) {
        item.IS_ERROR = 'N';
      }

      for (let a = 0; a < line.items.length; a++) {
        const item = line.items[a];

        if (line.IS_DISPLAY === 'Y') {
          if (item.IS_ENABLED !== 'N') {
            // Mandatory validation: if any other value entered, mandatory fields must be filled
            if (item.IS_MANDATORY === 'Y' && this.isEmpty(item) && this.isAnyOtherValueEntered(item, line.items)) {
              this.setErrorAttribs(line, item, 'Mandatory filed(s) are missing', requiredMsgAlreadyAdded);
              requiredMsgAlreadyAdded = true;
            }

            // CUSTOM_VALIDATION: K2 UI_S1 ALONE
            if (this.formSchd.includes('K2') && line.LINE_SEC === 'UI_S1') {
              // If TEXT is not empty and previous checkbox is not '1' → error
              if (item.UI_COMP === 'TEXT' && this.isNotEmpty(item) && a > 0 && line.items[a - 1].CELL_VALUE !== '1') {
                this.setErrorAttribs(line, item, 'Enter EIN only if \'Yes\' is checked', false);
              }
              // If checkbox is '1' and next TEXT is empty → error
              if (item.UI_COMP === 'CHK_BX' && item.CELL_VALUE === '1' && a + 1 < line.items.length && this.isEmpty(line.items[a + 1])) {
                this.setErrorAttribs(line, item, 'If \'Yes\' enter EIN', false);
              }
            }
          }
        }
      }
    }
  }

  private setErrorAttribs(line: GroupedFormLine, col: FormLineItem, errMsg: string, requiredMsgAlreadyAdded: boolean): void {
    if (line.ERR_MSG === undefined) {
      line.ERR_MSG = '<ol></ol>';
    }
    col.IS_ERROR = 'Y';
    this.isValidationError = true;
    if (!requiredMsgAlreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', '<li>' + errMsg + '</li></ol>');
    }
    this.showErrCol = true;
  }

  private buildDataToSave(groupedFormLinesObj: GroupedFormLine[]): any[] {
    const dataToSave: any[] = [];

    for (const line of groupedFormLinesObj) {
      for (const item of line.items) {
        const returnObj: any = {};
        let cellValue = item.CELL_VALUE;

        if (item.IS_ENABLED !== 'N') {
          if (item.UI_COMP === 'DATE') {
            if (this.isNotEmpty(item) && cellValue instanceof Date && !isNaN(cellValue.getTime())) {
              // Format date as YYYY-M-D (without leading zeros) to match original AngularJS
              returnObj.CELL_VALUE = `${cellValue.getFullYear()}-${cellValue.getMonth() + 1}-${cellValue.getDate()}`;
            } else {
              returnObj.CELL_VALUE = '';
            }
          } else if (item.UI_COMP === 'NUMERIC') {
            // Convert to number for NUMERIC fields, empty values should be null
            if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
              returnObj.CELL_VALUE = Number(cellValue);
            } else {
              returnObj.CELL_VALUE = null; // Empty NUMERIC = null (matching original)
            }
          } else if (item.UI_COMP === 'CHK_BX') {
            // Checkboxes: true/1/"1" → "1", false/0/"0"/empty → "0" (matching original AngularJS)
            returnObj.CELL_VALUE = (cellValue === true || cellValue === 1 || cellValue === '1') ? '1' : '0';
          } else if (item.UI_COMP === 'DROP_DOWN') {
            // Dropdowns: empty value should be null (matching original AngularJS)
            if (cellValue === null || cellValue === undefined || cellValue === '') {
              returnObj.CELL_VALUE = null;
            } else {
              returnObj.CELL_VALUE = cellValue;
            }
          } else {
            returnObj.CELL_VALUE = cellValue;
          }
        }

        returnObj.SCHD_KEY = item.SCHD_KEY;
        returnObj.LINE_KEY = item.LINE_KEY;
        returnObj.COL_KEY = item.COL_KEY;
        returnObj.ADJ_KEY = item.ADJ_KEY;
        returnObj.SNO = item.SNO;

        // FIX: Match original AngularJS pattern (P7S2, P12, P11 controllers)
        // Set OPERATION_TYPE UNCONDITIONALLY first, then handle special cases
        // This ensures items with cleared values (OPERATION_TYPE='U' but empty value) are still sent to API
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE;

        if (line.IS_LINE_MULTI === 'Y') {
          if (item.CELL_VALUE !== null && item.OPERATION_TYPE === undefined) {
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'N') {
            returnObj.OPERATION_TYPE = null;
          }
        } else {
          if (item.OPERATION_TYPE === undefined && item.CELL_VALUE !== null) {
            // New value being inserted
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            // After insert, mark as update for future saves
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'D') {
            // Deletion
            returnObj.OPERATION_TYPE = 'D';
            item.OPERATION_TYPE = undefined;
          } else if (item.OPERATION_TYPE === 'N') {
            // Disabled/not saveable
            returnObj.OPERATION_TYPE = null;
          }
          // Note: When OPERATION_TYPE is 'U', it stays 'U' (set unconditionally above)
          // This allows clearing values to be sent to API
        }

        if (returnObj.OPERATION_TYPE !== null && returnObj.OPERATION_TYPE !== undefined) {
          dataToSave.push(returnObj);
        }
      }
    }

    return dataToSave;
  }

  // Helper for date change in template - uses parseAsLocalDate to avoid timezone shift
  onDateChange(item: FormLineItem, dateString: string): void {
    if (dateString) {
      item.CELL_VALUE = parseAsLocalDate(dateString);
    } else {
      item.CELL_VALUE = '';
    }
  }

  // Helper to get rows for a specific section
  getRowsForSection(section: string): GroupedFormLine[] {
    return this.groupedFormLines.filter(l => l.IS_DISPLAY === 'Y' && l.LINE_SEC === section);
  }

  // Helper to get column headers for a specific section
  // COL_SEC can be comma-separated (e.g., "UI_S1,UI_S2") so we use includes()
  getColHeadersForSection(section: string): ColumnHeaderData[] {
    return this.colHeaderData
      .filter(c => c.COL_SEC?.includes(section))
      .sort((a, b) => (a.COL_PRIORITY || 0) - (b.COL_PRIORITY || 0));
  }

  // Check if section exists
  hasSection(section: string): boolean {
    return this.colHeaderSections.has(section);
  }

  // Track by function for ngFor
  trackByLine(index: number, line: GroupedFormLine): string {
    return `${line.LINE_KEY}_${line.SNO}_${line.IS_DISPLAY}`;
  }

  trackByCol(index: number, item: FormLineItem): number {
    return item.COL_KEY;
  }

  trackByColHeader(index: number, col: ColumnHeaderData): number {
    return col.COL_KEY;
  }
}
